import React, { useRef, useState } from 'react';
import { Toast } from 'primereact/toast';
import { Button } from 'primereact/button';
import { ListBox } from 'primereact/listbox';
import { InputText } from 'primereact/inputtext';
import { TabView, TabPanel } from 'primereact/tabview';
import { Message } from 'primereact/message';
import { ProgressSpinner } from 'primereact/progressspinner';
import { confirmDialog, ConfirmDialog } from 'primereact/confirmdialog';
import { useLanguage } from '../../../../contexts/LanguageContext';
import { useRoleManagementData } from './hooks/useRoleManagementData';
import PermissionMatrix from './components/PermissionMatrix';
import RoleSettingsForm from './components/RoleSettingsForm';
import CreateRoleDialog from './components/CreateRoleDialog';
import '../authorization-v2.scss';

const apiErrorMessage = (error, fallback) => error?.data?.message || error?.data?.errors?.join?.(', ') || error?.message || fallback;

const RoleManagement = () => {
    const { th } = useLanguage();
    const toast = useRef(null);
    const [createVisible, setCreateVisible] = useState(false);
    const [activeTab, setActiveTab] = useState(0);
    const data = useRoleManagementData();

    const notify = (severity, detail) => toast.current?.show({ severity, summary: th(severity === 'success' ? 'common.success' : 'common.error'), detail, life: severity === 'success' ? 3000 : 6000 });

    const run = async (action, successKey, errorKey) => {
        try {
            await action();
            notify('success', th(successKey));
        } catch (error) {
            notify('error', apiErrorMessage(error, th(errorKey)));
        }
    };

    const selectRole = (roleId) => {
        if (!roleId || roleId === data.selectedRoleId) return;
        if (!data.isDirty) {
            data.setSelectedRoleId(roleId);
            return;
        }
        confirmDialog({
            header: th('authorizationV2.matrix.unsavedHeader'),
            message: th('authorizationV2.matrix.unsavedMessage'),
            icon: 'pi pi-exclamation-triangle',
            acceptLabel: th('common.yes'),
            rejectLabel: th('common.no'),
            accept: () => data.setSelectedRoleId(roleId)
        });
    };

    const handleCreate = async (values) => {
        try {
            await data.createNewRole(values);
            setCreateVisible(false);
            setActiveTab(1);
            notify('success', th('authorizationV2.role.createSuccess'));
        } catch (error) {
            notify('error', apiErrorMessage(error, th('authorizationV2.role.createError')));
        }
    };

    const roleTemplate = (role) => (
        <div className="flex align-items-center gap-2">
            <i className="pi pi-id-card text-color-secondary" />
            <span>{role.name}</span>
        </div>
    );

    const renderDetail = () => {
        if (!data.selectedRoleId) {
            return (
                <div className="flex flex-column align-items-center justify-content-center text-color-secondary p-6">
                    <i className="pi pi-arrow-left text-3xl mb-3" />
                    <span>{th('authorizationV2.role.selectRole')}</span>
                </div>
            );
        }
        if (data.settingsLoading || data.permissionsLoading || data.catalogLoading) {
            return (
                <div className="flex justify-content-center p-6">
                    <ProgressSpinner style={{ width: '3rem', height: '3rem' }} />
                </div>
            );
        }
        return (
            <>
                <div className="flex align-items-center justify-content-between mb-3">
                    <div>
                        <h3 className="m-0">{data.selectedRole?.name}</h3>
                        {data.settings?.description && <small className="text-color-secondary">{data.settings.description}</small>}
                    </div>
                </div>
                {data.isSystemRole && <Message severity="info" className="w-full mb-3 justify-content-start" text={th('authorizationV2.role.systemRoleInfo')} />}
                <TabView activeIndex={activeTab} onTabChange={(e) => setActiveTab(e.index)}>
                    <TabPanel header={th('authorizationV2.role.generalTab')} leftIcon="pi pi-sliders-h mr-2">
                        <RoleSettingsForm
                            settings={data.settings}
                            catalog={data.catalog}
                            readOnly={data.isSystemRole}
                            saving={data.savingSettings}
                            onSave={(values) => run(() => data.saveSettings(values), 'authorizationV2.role.settingsSaved', 'authorizationV2.role.settingsSaveError')}
                        />
                    </TabPanel>
                    <TabPanel header={th('authorizationV2.role.matrixTab')} leftIcon="pi pi-table mr-2">
                        {!data.isSystemRole && (
                            <div className="flex justify-content-end gap-2 mb-3 authz-v2-sticky-actions">
                                <Button type="button" label={th('authorizationV2.matrix.reset')} icon="pi pi-undo" outlined disabled={!data.isDirty || data.savingPermissions} onClick={data.resetPermissions} />
                                <Button
                                    type="button"
                                    label={th('authorizationV2.matrix.save')}
                                    icon="pi pi-save"
                                    disabled={!data.isDirty}
                                    loading={data.savingPermissions}
                                    onClick={() => run(data.savePermissions, 'authorizationV2.matrix.saveSuccess', 'authorizationV2.matrix.saveError')}
                                />
                            </div>
                        )}
                        <PermissionMatrix
                            catalog={data.catalog}
                            grants={data.grants}
                            readOnly={data.isSystemRole}
                            onToggleAction={data.toggleAction}
                            onToggleResource={data.setResourceActions}
                            onToggleModule={data.setModuleActions}
                            onScopeChange={data.setResourceScope}
                        />
                    </TabPanel>
                </TabView>
            </>
        );
    };

    return (
        <div className="card authz-v2">
            <Toast ref={toast} />
            <ConfirmDialog />
            <div className="flex justify-content-between align-items-center mb-4">
                <div>
                    <h1 className="m-0">{th('authorizationV2.role.title')}</h1>
                    <small className="text-color-secondary">{th('authorizationV2.role.subtitle')}</small>
                </div>
                <Button type="button" label={th('authorizationV2.role.newRole')} icon="pi pi-plus" onClick={() => setCreateVisible(true)} />
            </div>

            {data.rolesError && <Message severity="error" className="w-full mb-3" text={apiErrorMessage(data.rolesError, th('authorizationV2.role.loadError'))} />}

            <div className="grid">
                <div className="col-12 lg:col-3">
                    <span className="p-input-icon-left w-full mb-2">
                        <i className="pi pi-search" />
                        <InputText className="w-full" value={data.roleFilter} onChange={(e) => data.setRoleFilter(e.target.value)} placeholder={th('authorizationV2.role.searchPlaceholder')} />
                    </span>
                    <ListBox
                        value={data.selectedRoleId}
                        options={data.roles}
                        optionLabel="name"
                        optionValue="id"
                        itemTemplate={roleTemplate}
                        onChange={(e) => selectRole(e.value)}
                        className="w-full"
                        listStyle={{ maxHeight: '65vh' }}
                        emptyMessage={data.rolesLoading ? th('common.loading') : th('authorizationV2.role.empty')}
                    />
                </div>
                <div className="col-12 lg:col-9">{renderDetail()}</div>
            </div>

            <CreateRoleDialog visible={createVisible} catalog={data.catalog} saving={data.creatingRole} onHide={() => setCreateVisible(false)} onCreate={handleCreate} />
        </div>
    );
};

export default RoleManagement;
